/**
 * Dashboard Service - Abstracts dashboard storage operations
 * Uses Supabase for persistent storage
 */

import { supabase } from '../config/supabase';
import { DASHBOARD_GRID_UNLIMITED_MAX_H } from '../components/coach/dashboard-builder/dashboardGridLayout';

export interface DashboardFeature {
  id: string;
  featureId: string;
  displayOrder: number;
  gridX: number;
  gridY: number;
  gridW: number;
  gridH: number;
  minW: number;
  minH: number;
  maxW: number;
  /** Legacy DB field only — not applied to react-grid-layout (no height cap). */
  maxH?: number;
  featureConfig?: Record<string, unknown>;
}

export interface Dashboard {
  id: string;
  name: string;
  features: DashboardFeature[];
  sessionId: string | null; // Session ID is optional
  layoutMode: 'vertical' | 'grid';
  createdAt: string;
  updatedAt?: string;
  isDemo?: boolean;
  coachId?: string;
}

/** Shared journey template: read-only unless the signed-in coach owns the row. */
export function isDashboardReadOnly(
  dashboard: Pick<Dashboard, 'isDemo' | 'coachId'>,
  userId: string | undefined
): boolean {
  if (!dashboard.isDemo) return false;
  if (!userId || !dashboard.coachId) return true;
  return dashboard.coachId !== userId;
}

// Database types matching our migration schema
interface CoachDashboardRow {
  id: string;
  coach_id: string;
  name: string;
  default_session_id: string | null;
  layout_mode: string;
  theme_config: unknown;
  created_at: string;
  updated_at: string;
}

interface DashboardWithFeatures {
  id: string;
  name: string;
  coach_id?: string;
  is_demo?: boolean;
  default_session_id: string | null;
  layout_mode: string;
  created_at: string;
  updated_at: string;
  coach_dashboard_features: Array<{
    id: string;
    feature_id: string;
    display_order: number;
    grid_x: number;
    grid_y: number;
    grid_w: number;
    grid_h: number;
    min_w: number;
    min_h: number;
    max_w: number;
    max_h: number;
    feature_config: Record<string, unknown> | null;
  }>;
}

export const dashboardService = {
  /**
   * Get all dashboards for the current authenticated user
   */
  async getAll(): Promise<Dashboard[]> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        console.warn('No authenticated user found');
        return [];
      }

      // Query dashboards with their features in a single query
      const { data: dashboards, error: dashboardsError } = await supabase
        .from('coach_dashboards')
        .select(
          `
          id,
          name,
          coach_id,
          is_demo,
          default_session_id,
          created_at,
          updated_at,
          coach_dashboard_features (
            id,
            feature_id,
            display_order,
            grid_x,
            grid_y,
            grid_w,
            grid_h,
            min_w,
            min_h,
            max_w,
            max_h,
            feature_config
          )
        `
        )
        .or(`coach_id.eq.${user.user.id},is_demo.eq.true`)
        .order('created_at', { ascending: false })
        .returns<DashboardWithFeatures[]>();

      if (dashboardsError) {
        console.error('Error loading dashboards:', dashboardsError);
        return [];
      }

      // Transform to Dashboard interface
      return (dashboards || []).map((dashboard) => ({
        id: dashboard.id,
        name: dashboard.name,
        features: (dashboard.coach_dashboard_features || [])
          .sort((a, b) => a.display_order - b.display_order)
          .map((f) => ({
            id: f.id,
            featureId: f.feature_id,
            displayOrder: f.display_order,
            gridX: f.grid_x,
            gridY: f.grid_y,
            gridW: f.grid_w,
            gridH: f.grid_h,
            minW: f.min_w,
            minH: f.min_h,
            maxW: f.max_w,
            featureConfig: f.feature_config,
          })),
        sessionId: dashboard.default_session_id || null,
        layoutMode: dashboard.layout_mode as 'vertical' | 'grid',
        createdAt: dashboard.created_at,
        updatedAt: dashboard.updated_at,
        isDemo: dashboard.is_demo === true,
        coachId: dashboard.coach_id,
      }));
    } catch (error) {
      console.error('Error loading dashboards:', error);
      return [];
    }
  },

  async canMutate(id: string): Promise<boolean> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return false;

      const { data, error } = await supabase
        .from('coach_dashboards')
        .select('coach_id')
        .eq('id', id)
        .maybeSingle();

      if (error || !data) return false;
      return data.coach_id === user.user.id;
    } catch {
      return false;
    }
  },

  /**
   * Get dashboard by ID
   */
  async getById(id: string): Promise<Dashboard | null> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        console.warn('No authenticated user found');
        return null;
      }

      const { data: dashboard, error } = await supabase
        .from('coach_dashboards')
        .select(
          `
          id,
          name,
          coach_id,
          is_demo,
          default_session_id,
          layout_mode,
          created_at,
          updated_at,
          coach_dashboard_features (
            id,
            feature_id,
            display_order,
            grid_x,
            grid_y,
            grid_w,
            grid_h,
            min_w,
            min_h,
            max_w,
            max_h,
            feature_config
          )
        `
        )
        .eq('id', id)
        .maybeSingle<DashboardWithFeatures>();

      if (error) {
        console.error('Error loading dashboard:', error);
        return null;
      }

      if (!dashboard) return null;

      return {
        id: dashboard.id,
        name: dashboard.name,
        features: (dashboard.coach_dashboard_features || [])
          .sort((a, b) => a.display_order - b.display_order)
          .map((f) => ({
            id: f.id,
            featureId: f.feature_id,
            displayOrder: f.display_order,
            gridX: f.grid_x,
            gridY: f.grid_y,
            gridW: f.grid_w,
            gridH: f.grid_h,
            minW: f.min_w,
            minH: f.min_h,
            maxW: f.max_w,
            featureConfig: f.feature_config,
          })),
        sessionId: dashboard.default_session_id || null,
        layoutMode: dashboard.layout_mode as 'vertical' | 'grid',
        createdAt: dashboard.created_at,
        updatedAt: dashboard.updated_at,
        isDemo: dashboard.is_demo === true,
        coachId: dashboard.coach_id,
      };
    } catch (error) {
      console.error('Error loading dashboard:', error);
      return null;
    }
  },

  /**
   * Create new dashboard
   */
  async create(dashboard: Omit<Dashboard, 'id' | 'createdAt'>): Promise<string | null> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        console.error('No authenticated user found');
        return null;
      }

      // Insert dashboard
      const { data: newDashboards, error: dashboardError } = await supabase
        .from('coach_dashboards')
        .insert({
          coach_id: user.user.id,
          name: dashboard.name,
          default_session_id: dashboard.sessionId || null,
          layout_mode: dashboard.layoutMode || 'grid',
        })
        .select()
        .returns<CoachDashboardRow[]>();

      const newDashboard = newDashboards?.[0];

      if (dashboardError || !newDashboard) {
        console.error('Error creating dashboard:', dashboardError);
        return null;
      }

      // Insert features
      if (dashboard.features.length > 0) {
        const features = dashboard.features.map((feature) => ({
          dashboard_id: newDashboard.id,
          feature_id: feature.featureId,
          display_order: feature.displayOrder,
          grid_x: feature.gridX,
          grid_y: feature.gridY,
          grid_w: feature.gridW,
          grid_h: feature.gridH,
          min_w: feature.minW,
          min_h: feature.minH,
          max_w: feature.maxW,
          max_h: DASHBOARD_GRID_UNLIMITED_MAX_H,
          feature_config: feature.featureConfig,
        }));

        const { error: featuresError } = await supabase
          .from('coach_dashboard_features')
          .insert(features);

        if (featuresError) {
          console.error('Error creating dashboard features:', featuresError);
          // Rollback: delete the dashboard
          await supabase.from('coach_dashboards').delete().eq('id', newDashboard.id);
          return null;
        }
      }

      return newDashboard.id;
    } catch (error) {
      console.error('Error creating dashboard:', error);
      return null;
    }
  },

  /**
   * Update existing dashboard
   */
  async update(
    id: string,
    updates: Partial<Omit<Dashboard, 'id' | 'createdAt'>>
  ): Promise<boolean> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        console.error('No authenticated user found');
        return false;
      }

      if (!(await this.canMutate(id))) {
        console.warn('Cannot update dashboard: not owned by current coach', id);
        return false;
      }

      // Update dashboard metadata
      const dashboardUpdates: Partial<CoachDashboardRow> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name !== undefined) {
        dashboardUpdates.name = updates.name;
      }
      if (updates.sessionId !== undefined) {
        dashboardUpdates.default_session_id = updates.sessionId || null;
      }
      if (updates.layoutMode !== undefined) {
        dashboardUpdates.layout_mode = updates.layoutMode;
      }

      const { error: updateError } = await supabase
        .from('coach_dashboards')
        .update(dashboardUpdates)
        .eq('id', id)
        .eq('coach_id', user.user.id);

      if (updateError) {
        console.error('Error updating dashboard:', updateError);
        return false;
      }

      // Update features if provided
      if (updates.features !== undefined) {
        // Delete existing features
        const { error: deleteError } = await supabase
          .from('coach_dashboard_features')
          .delete()
          .eq('dashboard_id', id);

        if (deleteError) {
          console.error('Error deleting old features:', deleteError);
          return false;
        }

        // Insert new features
        if (updates.features.length > 0) {
          const features = updates.features.map((feature) => ({
            dashboard_id: id,
            feature_id: feature.featureId,
            display_order: feature.displayOrder,
            grid_x: feature.gridX,
            grid_y: feature.gridY,
            grid_w: feature.gridW,
            grid_h: feature.gridH,
            min_w: feature.minW,
            min_h: feature.minH,
            max_w: feature.maxW,
            max_h: DASHBOARD_GRID_UNLIMITED_MAX_H,
            feature_config: feature.featureConfig,
          }));

          const { error: insertError } = await supabase
            .from('coach_dashboard_features')
            .insert(features);

          if (insertError) {
            console.error('Error inserting new features:', insertError);
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      console.error('Error updating dashboard:', error);
      return false;
    }
  },

  /**
   * Delete dashboard
   */
  async delete(id: string): Promise<boolean> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        console.error('No authenticated user found');
        return false;
      }

      if (!(await this.canMutate(id))) {
        console.warn('Cannot delete dashboard: not owned by current coach', id);
        return false;
      }

      const { error } = await supabase
        .from('coach_dashboards')
        .delete()
        .eq('id', id)
        .eq('coach_id', user.user.id);

      if (error) {
        console.error('Error deleting dashboard:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      return false;
    }
  },
};
